import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

// Get the main platform URL based on environment
const getMainPlatformUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  return 'https://ai.app.platform.halisoft.biz';
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });
  const mainPlatformUrl = getMainPlatformUrl();

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    localStorage.setItem('darkMode', newValue.toString());
  };

  // Apply dark mode class to body
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  const plans = [
    {
      id: 'free',
      name: 'FREE',
      price: 0,
      quota: 100,
      features: [
        '100 risk analyses / month',
        'Access to basic service',
        'Email support',
        'Standard reports'
      ],
      highlighted: false
    },
    {
      id: 'starter',
      name: 'STARTER',
      price: 9.99,
      quota: 1000,
      features: [
        '1,000 risk analyses / month',
        'Full service access',
        'Priority support',
        'Detailed reports',
        'PDF export',
        'API access'
      ],
      highlighted: true
    },
    {
      id: 'pro',
      name: 'PRO',
      price: 49.99,
      quota: 10000,
      features: [
        '10,000 risk analyses / month',
        'Unlimited feature access',
        '24/7 support',
        'Custom reports',
        'Multi-format export',
        'Unlimited API access',
        'Custom integrations',
        'Dedicated account manager'
      ],
      highlighted: false
    }
  ];

  const handleGetStarted = (planId) => {
    if (planId === 'free') {
      navigate('/register');
    } else {
      navigate('/register?plan=' + planId);
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div className={`landing-page ${darkMode ? 'dark-mode' : ''}`}>
      {/* Header - matching main platform style */}
      <header className={`landing-header ${darkMode ? 'dark' : ''}`}>
        <div className="container">
          <div className="logo" onClick={() => window.location.href = mainPlatformUrl} style={{ cursor: 'pointer' }}>
            <img src="/logo-halilab.svg" alt="HaliLab" className="logo-image" style={{height: '40px'}} />
            <div className="logo-text-group">
              <span className="logo-name">Hali<span className="logo-highlight">Lab</span></span>
              <span className="logo-sub">powered by HaliSoft</span>
            </div>
          </div>
          <nav className="nav">
            <a href={mainPlatformUrl} className="nav-link">Home</a>
            <a href={`${mainPlatformUrl}/about`} className="nav-link">About</a>
            <a href={`${mainPlatformUrl}/services`} className="nav-link">Services</a>
            <a href={`${mainPlatformUrl}/pricing`} className="nav-link highlight">Pricing</a>
            <a href={`${mainPlatformUrl}/contact`} className="nav-link">Contact</a>
            <button onClick={toggleDarkMode} className="darkmode-btn" title="Toggle Dark Mode">
              {darkMode ? '☀️' : '🌙'}
            </button>
            <button onClick={handleLogin} className="nav-btn">
              Login
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="gradient-text">Intelligent</span> Risk Analysis
            </h1>
            <p className="hero-subtitle">
              Analyze your financial risks in seconds with our advanced AI.
              Make informed decisions for your business.
            </p>
            <div className="hero-actions">
              <button onClick={() => handleGetStarted('free')} className="btn-hero-primary">
                🚀 Try For Free
              </button>
              <button onClick={() => navigate('/pricing')} className="btn-hero-secondary">
                📊 View Pricing
              </button>
            </div>
            <div className="hero-trust">
              <span className="trust-badge">✓ No credit card required</span>
              <span className="trust-badge">✓ 100 free analyses</span>
              <span className="trust-badge">✓ Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Why Choose HaliSoft?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🤖</div>
              <h3>Advanced AI</h3>
              <p>Analysis powered by Claude AI for accurate and fast results</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Instant Results</h3>
              <p>Get your analyses in seconds, not hours</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Secure & Confidential</h3>
              <p>Your data is protected with bank-level encryption</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📈</div>
              <h3>Detailed Reports</h3>
              <p>Visualize your data with clear and actionable reports</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔄</div>
              <h3>Integrations</h3>
              <p>Easily connect to your existing tools via API</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💬</div>
              <h3>Expert Support</h3>
              <p>Our team is here to help you every step of the way</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing" id="pricing">
        <div className="container">
          <h2 className="section-title">Choose Your Plan</h2>
          <p className="section-subtitle">
            Start for free, upgrade when you're ready
          </p>

          <div className="pricing-grid">
            {plans.map(plan => (
              <div
                key={plan.id}
                className={`pricing-card ${plan.highlighted ? 'highlighted' : ''}`}
              >
                {plan.highlighted && <div className="popular-badge">Most Popular</div>}

                <div className="pricing-header">
                  <h3 className="plan-name">{plan.name}</h3>
                  <div className="plan-price">
                    <span className="price-amount">{plan.price === 0 ? 'Free' : `$${plan.price}`}</span>
                    {plan.price > 0 && <span className="price-period">/month</span>}
                  </div>
                  <p className="plan-quota">{plan.quota.toLocaleString()} analyses / month</p>
                </div>

                <ul className="features-list">
                  {plan.features.map((feature, index) => (
                    <li key={index}>
                      <span className="check-icon">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleGetStarted(plan.id)}
                  className={`btn-plan ${plan.highlighted ? 'btn-plan-primary' : 'btn-plan-secondary'}`}
                >
                  {plan.price === 0 ? 'Start Free' : 'Get Started'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Your Risk Analysis?</h2>
            <p>Join hundreds of businesses that trust HaliSoft</p>
            <button onClick={() => handleGetStarted('free')} className="btn-cta">
              Get Started Now - Free
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>HaliSoft</h4>
              <p>Intelligent risk analysis solution for your business</p>
            </div>
            <div className="footer-section">
              <h4>Product</h4>
              <a href="#pricing">Pricing</a>
              <a href="/login">Login</a>
              <a href="/register">Sign Up</a>
            </div>
            <div className="footer-section">
              <h4>Support</h4>
              <a href="mailto:support@halisoft.com">Contact</a>
              <a href="/docs">Documentation</a>
              <a href="/faq">FAQ</a>
            </div>
            <div className="footer-section">
              <h4>Legal</h4>
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Service</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2024 HaliSoft. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating HaliGuide Button - Opens the real HaliGuide from main platform */}
      <button
        onClick={() => {
          const width = 420;
          const height = 700;
          const left = window.screenX + 50;
          const top = window.screenY + 100;
          window.open(
            `${mainPlatformUrl}/landing`,
            'HaliGuide',
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
          );
        }}
        className="haliguide-floating-btn"
        title="Open HaliGuide"
      >
        <span className="haliguide-icon">🤖</span>
        <span>HaliGuide</span>
      </button>
    </div>
  );
};

export default LandingPage;
